
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { supabase } from '../../../lib/supabase/client';
import { Bold, Italic, Underline, Strikethrough, List, ListOrdered, Link2 } from 'lucide-react';
import { CircleNotch, Code, Eye, EyeSlash, FloppyDisk, MagicWand, Newspaper, Tag, X } from '@phosphor-icons/react';
import { DialogHuelle, ImmoButton } from '../../../components/immobilien/ui';
import { FELD, Feld, HINWEIS, LABEL, MEHRZEILIG, Umschalter, WahlFeld } from './bereich/Formular';
import { VORLAGEN_TRIGGER } from '../../../lib/emails/vorlagenTrigger';
import { baueBriefVorschau } from '../../../lib/emails/emailHtml';

interface EmailVorlage {
  id: string;
  name: string;
  kategorie: string;
  beschreibung: string | null;
  betreff_template: string;
  html_template: string;
  platzhalter?: unknown;
  aktiv?: boolean;
  erstellt_von?: string | null;
  created_at?: string;
  updated_at?: string;
  ki_trigger?: string | null;
}

interface EmailVorlageFormularProps {
  vorlage?: EmailVorlage | null;
  onClose: () => void;
  onSaved: () => void;
  /** Mobil: 44-px-Ziele. */
  mobil?: boolean;
}

const KATEGORIE_OPTIONS = [
  { value: 'mieter', label: 'Mieter' },
  { value: 'eigentuemer', label: 'Eigentümer' },
  { value: 'dienstleister', label: 'Dienstleister/Handwerker' },
  { value: 'allgemein', label: 'Allgemein' },
  { value: 'zahlungserinnerung', label: 'Zahlungserinnerung' },
  { value: 'vertrag', label: 'Vertrag' },
  { value: 'wartung', label: 'Wartung' },
];

const PLATZHALTER_GROUPS = [
  {
    label: 'Mieter',
    items: [
      { key: 'mieter.anrede', label: 'Anrede' },
      { key: 'mieter.vorname', label: 'Vorname' },
      { key: 'mieter.nachname', label: 'Nachname' },
      { key: 'mieter.email', label: 'E-Mail' },
      { key: 'mieter.telefon', label: 'Telefon' },
    ],
  },
  {
    label: 'Eigentümer',
    items: [
      { key: 'eigentuemer.anrede', label: 'Anrede' },
      { key: 'eigentuemer.vorname', label: 'Vorname' },
      { key: 'eigentuemer.nachname', label: 'Nachname' },
      { key: 'eigentuemer.email', label: 'E-Mail' },
    ],
  },
  {
    label: 'Objekt',
    items: [
      { key: 'objekt.bezeichnung', label: 'Bezeichnung' },
      { key: 'objekt.strasse', label: 'Straße' },
      { key: 'objekt.hausnummer', label: 'Hausnummer' },
      { key: 'objekt.plz', label: 'PLZ' },
      { key: 'objekt.ort', label: 'Ort' },
    ],
  },
  {
    label: 'Allgemein',
    items: [
      { key: 'datum.heute', label: 'Heutiges Datum' },
      { key: 'absender.name', label: 'Absender-Name' },
      { key: 'absender.email', label: 'Absender-E-Mail' },
    ],
  },
  // Nur in der Handwerker-Beauftragung gefüllt (aus den Ticket-/Auftragsdaten,
  // via autoVorlageWerte in EmailVerfassen) — in normalen Mails werden sie beim
  // Senden entfernt.
  {
    label: 'Auftrag (Handwerker)',
    items: [
      { key: 'auftrag.kategorie', label: 'Kategorie' },
      { key: 'auftrag.beschreibung', label: 'Mangelbeschreibung' },
      { key: 'auftrag.einheit', label: 'Wohnung/Einheit' },
      { key: 'auftrag.eigentuemer', label: 'Eigentümer / WEG' },
    ],
  },
  {
    label: 'Meldender (Handwerker)',
    items: [
      { key: 'meldender.name', label: 'Name' },
      { key: 'meldender.rolle', label: 'Rolle' },
      { key: 'meldender.telefon', label: 'Telefon' },
      { key: 'meldender.email', label: 'E-Mail' },
    ],
  },
  // Nur in der Termin-Einladung gefüllt (Outlook-Sync rendert den Einladungs-Body
  // serverseitig aus dem Termin) — in normalen Mails werden sie beim Senden entfernt.
  {
    label: 'Termin (Einladung)',
    items: [
      { key: 'termin.titel', label: 'Titel' },
      { key: 'termin.beschreibung', label: 'Beschreibung' },
      { key: 'termin.datum', label: 'Datum (mit Wochentag)' },
      { key: 'termin.uhrzeit', label: 'Uhrzeit (Beginn)' },
      { key: 'termin.ende', label: 'Uhrzeit (Ende)' },
      { key: 'objekt.adresse', label: 'Objekt-Adresse (komplett)' },
    ],
  },
];

const BEISPIEL_DATEN: Record<string, string> = {
  'mieter.anrede': 'Herr',
  'mieter.vorname': 'Max',
  'mieter.nachname': 'Mustermann',
  'mieter.email': 'max@beispiel.de',
  'mieter.telefon': '0170 1234567',
  'eigentuemer.anrede': 'Frau',
  'eigentuemer.vorname': 'Anna',
  'eigentuemer.nachname': 'Vermieterin',
  'eigentuemer.email': 'anna@immo.de',
  'objekt.bezeichnung': 'Musterhaus',
  'objekt.strasse': 'Musterstr.',
  'objekt.hausnummer': '42',
  'objekt.plz': '10115',
  'objekt.ort': 'Berlin',
  'datum.heute': new Date().toLocaleDateString('de-DE'),
  'absender.name': 'ImmoSchwede Verwaltung',
  'absender.email': 'info@immoschwede.de',
  'auftrag.kategorie': 'Heizung',
  'auftrag.beschreibung': 'Heizung fällt aus, Thermostat vermutlich defekt.',
  'auftrag.einheit': 'Whg. 3, EG links',
  'auftrag.eigentuemer': 'WEG Musterstraße 42',
  'meldender.name': 'Max Mustermann',
  'meldender.rolle': 'Mieter',
  'meldender.telefon': '0170 1234567',
  'meldender.email': 'max@beispiel.de',
  'termin.titel': 'Begehung Musterstraße',
  'termin.beschreibung': 'Gemeinsame Begehung der Wohnung vor Übergabe.',
  'termin.datum': 'Mittwoch, 05.08.2026',
  'termin.uhrzeit': '10:00 Uhr',
  'termin.ende': '11:00 Uhr',
  'objekt.adresse': 'Musterstr. 42, 10115 Berlin',
};

function replacePlaceholders(text: string): string {
  let result = text;
  Object.entries(BEISPIEL_DATEN).forEach(([key, val]) => {
    result = result.replace(new RegExp(`\\{\\{${key.replace('.', '\\.')}\\}\\}`, 'g'), val);
  });
  return result;
}

const EmailVorlageFormular: React.FC<EmailVorlageFormularProps> = ({ vorlage, onClose, onSaved, mobil = false }) => {
  // Fehler im Dialog-Fuß statt alert() – gleiche Texte (Entscheidung 8).
  const [fehler, setFehler] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(vorlage?.name || '');
  const [kategorie, setKategorie] = useState(vorlage?.kategorie || 'allgemein');
  const [beschreibung, setBeschreibung] = useState(vorlage?.beschreibung || '');
  const [betreff, setBetreff] = useState(vorlage?.betreff_template || '');
  const [htmlBody, setHtmlBody] = useState(vorlage?.html_template || '');
  const [kiTrigger, setKiTrigger] = useState(vorlage?.ki_trigger || '');
  const [showPreview, setShowPreview] = useState(false);
  // Briefpapier-Vorschau: Standard-Briefpapier + Standard-Signatur, Vorlage im {{inhalt}}-Slot.
  const [mitBriefpapier, setMitBriefpapier] = useState(true);
  const [briefpapierHtml, setBriefpapierHtml] = useState<string | null>(null); // null = noch nicht geladen
  const [signaturHtml, setSignaturHtml] = useState('');
  const [modus, setModus] = useState<'visuell' | 'html'>('visuell');
  const [toolbar, setToolbar] = useState<{ top: number; left: number } | null>(null);

  const betreffRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const activeFieldRef = useRef<'betreff' | 'body'>('body');
  const previewRef = useRef<HTMLIFrameElement>(null);

  // Visuellen Editor beim Mounten und bei jedem Wechsel auf 'visuell' mit dem aktuellen HTML füllen.
  // Sanitizing: Vorlagen-HTML wird von Staff angelegt -> gespeichertes XSS möglich, daher vor dem
  // innerHTML-Setzen über DOMPurify säubern (script/iframe/object/embed/form verboten).
  useEffect(() => {
    if (modus === 'visuell' && editorRef.current) {
      editorRef.current.innerHTML = DOMPurify.sanitize(htmlBody, {
        USE_PROFILES: { html: true },
        FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modus]);

  // Standard-Briefpapier + -Signatur einmalig laden, sobald die Vorschau geöffnet wird.
  useEffect(() => {
    if (!showPreview || briefpapierHtml !== null) return;
    (async () => {
      const [bp, sig] = await Promise.all([
        supabase.from('email_briefpapier').select('html, ist_standard').order('ist_standard', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('email_signaturen').select('html_signatur, ist_standard').order('ist_standard', { ascending: false }).limit(1).maybeSingle(),
      ]);
      setBriefpapierHtml(bp.data?.html ?? '');
      setSignaturHtml(sig.data?.html_signatur ?? '');
    })();
  }, [showPreview, briefpapierHtml]);

  // Live-Vorschau (Platzhalter durch Beispieldaten ersetzt) — wahlweise im Briefbogen,
  // damit man die Mail exakt so sieht, wie der Empfänger sie bekommt.
  useEffect(() => {
    if (showPreview && previewRef.current && htmlBody) {
      const doc = previewRef.current.contentDocument;
      if (!doc) return;
      const inhalt = replacePlaceholders(htmlBody);
      const voll = mitBriefpapier && briefpapierHtml
        ? baueBriefVorschau(briefpapierHtml, inhalt, signaturHtml)
        : inhalt;
      doc.open(); doc.write(voll); doc.close();
    }
  }, [showPreview, htmlBody, mitBriefpapier, briefpapierHtml, signaturHtml]);

  // ── Rich-Text-Editor: Format-Toolbar bei Auswahl ──
  const updateToolbar = useCallback(() => {
    const sel = window.getSelection();
    const box = editorRef.current;
    if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !box) { setToolbar(null); return; }
    const range = sel.getRangeAt(0);
    if (!box.contains(range.commonAncestorContainer)) { setToolbar(null); return; }
    const rect = range.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) { setToolbar(null); return; }
    setToolbar({ top: rect.top - b.top - 46, left: Math.min(Math.max(rect.left - b.left + rect.width / 2, 90), b.width - 90) });
  }, []);

  const exec = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    if (editorRef.current) setHtmlBody(editorRef.current.innerHTML);
    updateToolbar();
  };
  const einfuegenLink = () => {
    const url = window.prompt('Link-URL (mit https://):', 'https://');
    if (url && url !== 'https://') exec('createLink', url);
  };

  const onEditorInput = () => { if (editorRef.current) setHtmlBody(editorRef.current.innerHTML); };

  // Eingefügtes HTML (Word/Web/Editoren) radikal bereinigen: nur Grundstruktur + Basis-
  // Formatierung bleibt, sämtliche Styles/Klassen/Datenattribute fliegen raus. Verhindert
  // Vorlagen mit zigtausend Zeichen Editor-Müll (z. B. ProseMirror-/--tw-*-Inline-Styles,
  // siehe Handwerkerbeauftragung: 47k → 2k Zeichen nach Bereinigung).
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (!html && !text) return;
    e.preventDefault();
    const clean = html
      ? DOMPurify.sanitize(html, {
          ALLOWED_TAGS: ['p', 'br', 'div', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3'],
          ALLOWED_ATTR: ['href'],
        })
      : text
          .split('\n')
          .map((z) => z.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
          .join('<br>');
    document.execCommand('insertHTML', false, clean);
    if (editorRef.current) setHtmlBody(editorRef.current.innerHTML);
  };

  // ── Platzhalter an der Cursorposition des aktiven Felds einfügen ──
  const insertPlatzhalter = (key: string) => {
    const ph = `{{${key}}}`;
    if (activeFieldRef.current === 'betreff' && betreffRef.current) {
      const el = betreffRef.current;
      const start = el.selectionStart ?? betreff.length;
      const end = el.selectionEnd ?? betreff.length;
      const next = betreff.substring(0, start) + ph + betreff.substring(end);
      setBetreff(next);
      setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + ph.length; }, 0);
      return;
    }
    if (modus === 'html' && bodyRef.current) {
      const ta = bodyRef.current;
      const start = ta.selectionStart ?? htmlBody.length;
      const end = ta.selectionEnd ?? htmlBody.length;
      const next = htmlBody.substring(0, start) + ph + htmlBody.substring(end);
      setHtmlBody(next);
      setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + ph.length; }, 0);
      return;
    }
    // Visueller Editor: an der aktuellen Cursorposition einfügen (mousedown-preventDefault erhält die Auswahl).
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertText', false, ph);
      setHtmlBody(editorRef.current.innerHTML);
    }
  };

  const handleSave = async () => {
    setFehler(null);
    if (!name.trim() || !betreff.trim()) { setFehler('Bitte Name und Betreff ausfüllen.'); return; }
    // Im visuellen Modus den aktuellen Editor-Stand übernehmen.
    const body = modus === 'visuell' && editorRef.current ? editorRef.current.innerHTML : htmlBody;
    setSaving(true);
    try {
      // Eindeutigkeit: eine Vorlage pro automatischer Aktion — bisherige Zuordnung lösen.
      if (kiTrigger.trim()) {
        await supabase.from('email_vorlagen').update({ ki_trigger: null })
          .eq('ki_trigger', kiTrigger.trim())
          .neq('id', vorlage?.id ?? '00000000-0000-0000-0000-000000000000');
      }
      const payload = {
        name: name.trim(),
        kategorie,
        beschreibung: beschreibung.trim() || null,
        betreff_template: betreff.trim(),
        html_template: body,
        updated_at: new Date().toISOString(),
        ki_trigger: kiTrigger.trim() || null,
      };
      if (vorlage) {
        const { error } = await supabase.from('email_vorlagen').update(payload).eq('id', vorlage.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('email_vorlagen').insert(payload);
        if (error) throw error;
      }
      onSaved();
    } catch (err: any) {
      setFehler('Fehler: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setSaving(false);
    }
  };

  const toolBtn = 'rounded p-1.5 text-white/70 hover:bg-white/10 hover:text-white transition-colors';
  const iconKnopf = (aktiv: boolean): React.CSSProperties => ({
    width: mobil ? 44 : 32, height: mobil ? 44 : 32, borderRadius: 10, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    background: aktiv ? 'var(--acc-soft)' : 'transparent', color: aktiv ? 'var(--acc)' : 'var(--ink2)',
  });

  return (
    <DialogHuelle
      titel={vorlage ? 'Vorlage bearbeiten' : 'Neue Vorlage'}
      untertitel="E-Mail-Template mit Platzhaltern"
      icon={MagicWand}
      breite={1100}
      laeuft={saving}
      onSchliessen={onClose}
      fuss={
        <div className="flex flex-wrap items-center w-full" style={{ gap: 10 }}>
          {fehler && <span role="alert" className="flex-1 min-w-0" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
          <span className="flex" style={{ gap: 8, marginLeft: 'auto' }}>
            <ImmoButton groesse={44} icon={X} onClick={onClose} disabled={saving}>Abbrechen</ImmoButton>
            <ImmoButton art="akzent" groesse={44} icon={saving ? CircleNotch : FloppyDisk} onClick={handleSave} disabled={saving}>
              {vorlage ? 'Speichern' : 'Erstellen'}
            </ImmoButton>
          </span>
        </div>
      }
    >
      <div className="flex flex-wrap" style={{ gap: 18 }}>
        {/* Formular */}
        <div className="flex flex-col min-w-0" style={{ flex: '2 1 420px', gap: 12 }}>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <Feld label="Name *">
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="z. B. Willkommen Mieter" style={FELD} />
            </Feld>
            <Feld label="Kategorie">
              <WahlFeld wert={kategorie} onChange={setKategorie} ariaLabel="Kategorie">
                {KATEGORIE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </WahlFeld>
            </Feld>
          </div>

          <Feld label="Beschreibung">
            <textarea value={beschreibung} onChange={e => setBeschreibung(e.target.value)} rows={2} placeholder="Kurze Beschreibung der Vorlage…" style={MEHRZEILIG} />
          </Feld>

          <Feld label="Automatische Aktion (optional)"
            hinweis="Die Vorlage wird bei dieser Aktion automatisch verwendet — eine Vorlage pro Aktion (Speichern übernimmt die Zuordnung von einer evtl. bisherigen Vorlage).">
            <WahlFeld wert={kiTrigger} onChange={setKiTrigger} ariaLabel="Automatische Aktion">
              <option value="">— keine —</option>
              {VORLAGEN_TRIGGER.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              {kiTrigger && !VORLAGEN_TRIGGER.some(t => t.key === kiTrigger) && (
                <option value={kiTrigger}>{kiTrigger} (unbekannter Trigger)</option>
              )}
            </WahlFeld>
          </Feld>

          <Feld label="Betreff *">
            <input
              ref={betreffRef}
              type="text"
              value={betreff}
              onChange={e => setBetreff(e.target.value)}
              onFocus={() => { activeFieldRef.current = 'betreff'; }}
              placeholder="z. B. Willkommen {{mieter.vorname}} {{mieter.nachname}}"
              style={FELD}
            />
          </Feld>

          {/* Nachricht — Visuell / HTML */}
          <div className="flex flex-col" style={{ gap: 6 }}>
            <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
              <span className="flex-1" style={LABEL}>Nachricht</span>
              <Umschalter hoehe={mobil ? 44 : 28} wert={modus} onChange={setModus}
                optionen={[{ key: 'visuell', label: 'Visuell', icon: <MagicWand size={12} weight="bold" /> }, { key: 'html', label: 'HTML', icon: <Code size={12} weight="bold" /> }]} />
              {showPreview && (
                <button type="button" onClick={() => setMitBriefpapier(v => !v)} title="Vorschau mit Briefpapier + Signatur" aria-label="Vorschau mit Briefpapier + Signatur" aria-pressed={mitBriefpapier} style={iconKnopf(mitBriefpapier)}>
                  <Newspaper size={16} />
                </button>
              )}
              <button type="button" onClick={() => setShowPreview(!showPreview)} title="Live-Vorschau" aria-label="Live-Vorschau" aria-pressed={showPreview} style={iconKnopf(showPreview)}>
                {showPreview ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {modus === 'visuell' ? (
              <div className="relative" style={{ borderRadius: 14, background: '#fff', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={onEditorInput}
                  onPaste={handlePaste}
                  onMouseUp={updateToolbar}
                  onKeyUp={updateToolbar}
                  onFocus={() => { activeFieldRef.current = 'body'; }}
                  onBlur={() => setTimeout(() => setToolbar(null), 150)}
                  data-placeholder="Text schreiben, formatieren und Platzhalter einfügen…"
                  className="min-h-[240px] px-4 py-3 outline-none text-sm leading-relaxed text-gray-800 [&_a]:text-[#1a1d24] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-gray-300 [&:empty]:before:pointer-events-none"
                />
                {toolbar && (
                  <div className="absolute z-30 flex items-center gap-0.5 rounded-lg bg-[#1a1d24] border border-white/10 px-1 py-1 shadow-xl -translate-x-1/2" style={{ top: toolbar.top, left: toolbar.left }} onMouseDown={e => e.preventDefault()}>
                    <button type="button" title="Fett" onClick={() => exec('bold')} className={toolBtn}><Bold size={14} /></button>
                    <button type="button" title="Kursiv" onClick={() => exec('italic')} className={toolBtn}><Italic size={14} /></button>
                    <button type="button" title="Unterstrichen" onClick={() => exec('underline')} className={toolBtn}><Underline size={14} /></button>
                    <button type="button" title="Durchgestrichen" onClick={() => exec('strikeThrough')} className={toolBtn}><Strikethrough size={14} /></button>
                    <span className="mx-0.5 h-4 w-px bg-white/15" />
                    <button type="button" title="Aufzählung" onClick={() => exec('insertUnorderedList')} className={toolBtn}><List size={14} /></button>
                    <button type="button" title="Nummerierte Liste" onClick={() => exec('insertOrderedList')} className={toolBtn}><ListOrdered size={14} /></button>
                    <span className="mx-0.5 h-4 w-px bg-white/15" />
                    <button type="button" title="Link einfügen" onClick={einfuegenLink} className={toolBtn}><Link2 size={14} /></button>
                  </div>
                )}
              </div>
            ) : (
              <textarea
                ref={bodyRef}
                value={htmlBody}
                onChange={e => setHtmlBody(e.target.value)}
                onFocus={() => { activeFieldRef.current = 'body'; }}
                style={{ ...MEHRZEILIG, height: 240, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}
                placeholder={'<div>\n  <h2>Willkommen, {{mieter.vorname}}!</h2>\n  <p>Ihr neues Zuhause: {{objekt.strasse}} {{objekt.hausnummer}}</p>\n</div>'}
              />
            )}

            {/* Live-Vorschau */}
            {showPreview && (
              <div className="flex flex-col" style={{ gap: 8, marginTop: 6 }}>
                <span className="flex items-center" style={{ ...LABEL, gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ok)' }} />Live-Vorschau</span>
                <div style={{ borderRadius: 14, padding: '10px 14px', background: 'var(--fill2)' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>Betreff-Vorschau</span>
                  <p style={{ margin: '2px 0 0', fontSize: 14, fontWeight: 800 }}>{replacePlaceholders(betreff) || '(kein Betreff)'}</p>
                </div>
                <div className="overflow-hidden" style={{ borderRadius: 18, background: '#fff', boxShadow: '0 18px 40px -18px var(--shade)' }}>
                  {htmlBody ? (
                    <iframe ref={previewRef} sandbox="allow-same-origin" title="E-Mail-Vorschau" className="w-full border-0 block" style={{ height: mitBriefpapier ? 430 : 260 }} />
                  ) : (
                    <div className="flex items-center justify-center" style={{ height: 180, fontSize: 12, color: '#9ca3af' }}>Inhalt eingeben für Vorschau</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Platzhalter */}
        <div className="flex flex-col min-w-0" style={{ flex: '1 1 260px', gap: 8 }}>
          <span className="flex items-center" style={{ ...LABEL, gap: 6 }}><Tag size={13} />Platzhalter einfügen</span>
          <div className="flex flex-col" style={{ gap: 10, borderRadius: 18, padding: 12, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
            {PLATZHALTER_GROUPS.map(group => (
              <div key={group.label} className="flex flex-wrap items-center" style={{ gap: 5 }}>
                <span style={{ width: '100%', fontSize: 11, fontWeight: 800, color: 'var(--ink2)' }}>{group.label}</span>
                {group.items.map(item => (
                  <button
                    key={item.key}
                    type="button"
                    title={`{{${item.key}}}`}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => insertPlatzhalter(item.key)}
                    className="cursor-pointer"
                    style={{ height: mobil ? 44 : 26, padding: '0 9px', borderRadius: 13, border: 'none', fontSize: 11, fontWeight: 700, background: 'var(--ok-soft)', color: 'var(--ok)' }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <span style={HINWEIS}>Klick fügt den Platzhalter an der Cursorposition im Betreff- oder Nachrichtenfeld ein.</span>
        </div>
      </div>
    </DialogHuelle>
  );
};

export default EmailVorlageFormular;
